import ApartmentIcon from '@mui/icons-material/Apartment'
import ApprovalIcon from '@mui/icons-material/Approval'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChecklistIcon from '@mui/icons-material/Checklist'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import HouseIcon from '@mui/icons-material/House'
import MenuIcon from '@mui/icons-material/Menu'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import MuiAppBar from '@mui/material/AppBar'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import MuiDrawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { styled, Theme } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { useEffect, useMemo, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  MULTI_FAMILY_TABS,
  PLANS_TABS,
  VIEW_ONLY_FAVORITES_TABS,
  VIEW_ONLY_MULTI_FAMILY_TABS,
} from 'src/constants/plans.constants'
import { lennarTheme } from 'src/lennarTheme'
import {
  DASHBOARD,
  EXPORTS,
  EXPORTS_LABEL,
  familyTypes,
  FAVORITES,
  FAVORITES_LABEL,
  MULTI_FAMILY,
  MULTI_FAMILY_LABEL,
  PENDING_APPROVALS,
  PENDING_APPROVALS_LABEL,
  PENDING_PLANS,
  PENDING_PLANS_LABEL,
  REGIONAL_APPROVALS,
  REGIONAL_APPROVALS_LABEL,
  SINGLE_FAMILY,
  SINGLE_FAMILY_LABEL,
} from 'src/lib/constants'
import { getEnvVar } from 'src/lib/env'
import { ROUTES } from 'src/lib/routes'
import { usePendingRegionalApprovalCount } from 'src/redux/selectors/approvalSelectors'
import { setDrawerOpen } from 'src/redux/slice/drawerSlice'
import { setSelectedTab } from 'src/redux/slice/MultiFamilyPlanListingSlice'
import { clearAllPlansActiveElevations } from 'src/redux/slice/selectedPlansSlice'
import { RootState } from 'src/redux/stores/store'

import { useAppDispatch } from '../../hooks/useAppDispatch'
import { setDraftPlan } from '../../redux/slice/createPlanSlice'
import {
  resetPlanListing,
  setSinglePlansPageTab,
} from '../../redux/slice/planListingSlice'
import { getIsCreatePlanFormDirty } from '../../utils/planForm.util'
import { DialogBox } from '../Modals/DialogBox'
import { LennarLogoComponent } from '../Shared/LennarLogoComponent'
import { checkSearchAndCompareMode } from '../utils'
import { LocalBranchIndicator } from './LocalBranchIndicator'
import { ReleaseNoteBanner } from './ReleaseNoteBanner'
import SupportActions from './SupportActions'
import { UserAvatarAndSettingsMenu } from './UserAvatarAndSettingsMenu'

const drawerWidth = 240

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ open, theme }: { open: boolean; theme?: Theme }) => ({
  paddingRight: open ? drawerWidth : 0,
  zIndex: theme ? theme.zIndex.drawer + 1 : 0,
  ...(open && {
    marginLeft: drawerWidth,
  }),
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ open, theme }) => ({
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    position: 'relative',
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.sharp,
    }),
    whiteSpace: 'nowrap',
    width: drawerWidth,
    ...(!open && {
      overflowX: 'hidden',
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
      transition: theme.transitions.create('width', {
        duration: theme.transitions.duration.leavingScreen,
        easing: theme.transitions.easing.sharp,
      }),
      width: theme.spacing(7),
    }),
    '& .MuiList-root': {
      [theme.breakpoints.up('sm')]: {
        '& .MuiButtonBase-root': {
          paddingLeft: '20px',
        },
      },
    },
  },
}))

const isViewOnlyEnabled = getEnvVar('REACT_APP_VIEW_ONLY_ENABLED') === 'true'

export function Bars() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const singleTabValue = useSelector(
    (state: RootState) => state.planListing.singleTabValue
  )
  const selectedMultiFamilyTab = useSelector(
    (state: RootState) => state.multiFamilyPlanListing.multiTabValue
  )
  const selectedFavoritesTab = useSelector(
    (state: RootState) => state.FavoritePlans.favoritesTabValue
  )
  const {
    isCorporateCreateUser,
    isCorporateViewUser,
    isDivisionCreateUser,
    isDivisionViewUser,
  } = useSelector((state: RootState) => state.Auth)

  const { isCurrentUserAnApprover, isCurrentUserARegionalApprover } =
    useSelector((state: RootState) => state.plans)

  // LTGC-5711: how many regional plan approvals are waiting on THIS approver.
  // Reading the fetched queue alone left the badge stuck until something
  // refetched it — that array is never written by SignalR — so the count comes
  // from a selector that merges live broadcasts over the snapshot.
  const pendingRegionalApprovalCount = usePendingRegionalApprovalCount()

  const isDrawerOpen = useSelector((state: RootState) => state.drawer.open)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedItem, setSelectedItem] = useState<string | null>(null)

  // Read on click via useStore().getState() instead of subscribing: Bars is
  // mounted app-wide, and a draftPlan subscription re-rendered the whole nav
  // on every keystroke in the plan entry forms. useStore never triggers a
  // re-render; the values only gate the leave dialog.
  const reduxStore = useStore<RootState>()
  const hasUnsavedPlanChanges = (): boolean => {
    const {
      autoSavedPlans,
      isAutoSaveEnabled,
      multiElevationPlans,
      processedValidationErrors,
    } = reduxStore.getState().createPlan.draftPlan

    const isPlanFormRoute =
      window.location.pathname === '/create-plan' ||
      window.location.pathname.startsWith('/edit-plan') ||
      window.location.pathname.startsWith('/copy-plan')

    return isPlanFormRoute && isAutoSaveEnabled
      ? !isEmpty(autoSavedPlans) &&
          !isEqual(multiElevationPlans, autoSavedPlans)
      : getIsCreatePlanFormDirty(multiElevationPlans) &&
          processedValidationErrors?.length === 0
  }

  const isViewOnlyUser = isCorporateViewUser || isDivisionViewUser
  const searchAndCompareMode = checkSearchAndCompareMode()

  const shouldShowViewOnlyPage =
    isViewOnlyEnabled && (isViewOnlyUser || searchAndCompareMode)

  useEffect(() => {
    if (shouldShowViewOnlyPage) {
      dispatch(setDrawerOpen(true))
    }
  }, [
    dispatch,
    isCorporateViewUser,
    isDivisionViewUser,
    isViewOnlyEnabled,
    searchAndCompareMode,
  ])

  const pathToSelectedItemMap: Record<string, string> = {
    '/': DASHBOARD,
    [ROUTES.APPROVALS]: PENDING_APPROVALS,
    [ROUTES.EXPORTS]: EXPORTS,
    [ROUTES.PENDING_PLANS]: PENDING_PLANS,
    [ROUTES.REGIONAL_APPROVALS]: REGIONAL_APPROVALS,
  }
  const planRoutes = [
    ROUTES.PLANS,
    ROUTES.DIVISION,
    ROUTES.DRAFTS,
    ROUTES.FAVORITES,
    ROUTES.SHARED_WITH_ME,
  ]
  const favoritesRoutes = [
    ROUTES.FAVORITES_SHELLS,
    ROUTES.FAVORITES_UNITS,
    ROUTES.FAVORITES_SINGLE_FAMILY,
  ]
  // Synchronize selectedItem with current pathname
  useEffect(() => {
    // Check for plan routes that should highlight Single-Family or Multi-Family
    const isOnPlanRoute = planRoutes.some((route) => pathname.startsWith(route))
    const isFavorites = favoritesRoutes.some((route) =>
      pathname.startsWith(route)
    )
    if (isFavorites) {
      setSelectedItem(FAVORITES)
    } else if (isOnPlanRoute) {
      const planType = searchParams.get('plan-type') || familyTypes.SINGLE
      setSelectedItem(
        planType === familyTypes.SINGLE ? SINGLE_FAMILY : MULTI_FAMILY
      )
    } else {
      const selectedItemForPath = pathToSelectedItemMap[pathname]
      if (selectedItemForPath !== undefined) {
        setSelectedItem(selectedItemForPath)
      }
    }
  }, [pathname, searchParams])

  const toggleDrawer = () => {
    dispatch(setDrawerOpen(!isDrawerOpen))
  }

  const handleSaveAsDraft = async () => {
    dispatch(setDraftPlan({ key: 'hasPageChanged', value: true }))
    setOpenDialog(false)
  }

  const handleCloseDialog = () => {
    navigate('/')
    setOpenDialog(false)
  }

  const closeDialog = () => {
    setOpenDialog(false)
  }

  const navigateToRoute = (tabValue: string, planType: string) => {
    const tabRoutes: Record<string, string> = {
      [PLANS_TABS.ALL_PLANS]: ROUTES.PLANS,
      [PLANS_TABS.DIVISION]: ROUTES.DIVISION,
      [PLANS_TABS.DRAFTS]: ROUTES.DRAFTS,
      [PLANS_TABS.FAVORITES]: ROUTES.FAVORITES,
      [PLANS_TABS.SHARED_WITH_ME]: ROUTES.SHARED_WITH_ME,
      [VIEW_ONLY_MULTI_FAMILY_TABS.SHELL_PLANS]: ROUTES.SHELL_PLANS,
      [VIEW_ONLY_MULTI_FAMILY_TABS.UNIT_PLANS]: ROUTES.UNIT_PLANS,
    }

    const route = tabRoutes[tabValue]
    if (route) {
      navigate(`${route}?plan-type=${planType}`)
    }
  }

  const navigateToFavoritesRoute = () => {
    const { SHELL_PLANS, SINGLE_FAMILY_PLANS, UNIT_PLANS } =
      VIEW_ONLY_FAVORITES_TABS

    const tabRoutes = {
      [SHELL_PLANS]: ROUTES.FAVORITES_SHELLS,
      [SINGLE_FAMILY_PLANS]: ROUTES.FAVORITES_SINGLE_FAMILY,
      [UNIT_PLANS]: ROUTES.FAVORITES_UNITS,
    }

    const familyType = [SHELL_PLANS, UNIT_PLANS].includes(selectedFavoritesTab)
      ? familyTypes.MULTI
      : familyTypes.SINGLE

    const route = tabRoutes[selectedFavoritesTab]
    navigate(`${route}?plan-type=${familyType}`)
  }

  const isOnDetailedPlan = useMemo(() => {
    return window.location.pathname.includes(ROUTES.DETAILED_PLAN)
  }, [pathname])

  const getMultiFamilyTab = () => {
    if (!selectedMultiFamilyTab) {
      return shouldShowViewOnlyPage
        ? VIEW_ONLY_MULTI_FAMILY_TABS.SHELL_PLANS
        : MULTI_FAMILY_TABS.ALL_PLANS
    }

    if (shouldShowViewOnlyPage) {
      return selectedMultiFamilyTab in VIEW_ONLY_MULTI_FAMILY_TABS
        ? selectedMultiFamilyTab
        : VIEW_ONLY_MULTI_FAMILY_TABS.SHELL_PLANS
    } else {
      return selectedMultiFamilyTab
    }
  }

  return (
    <>
      <AppBar
        id="header"
        position="fixed"
        open={isDrawerOpen}
        sx={{ background: '#ffffff', left: 0, width: '100%' }}
      >
        <Toolbar
          sx={{
            pr: '24px', // keep right padding when drawer closed,
          }}
        >
          {!isOnDetailedPlan && (
            <IconButton
              edge="start"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                color: lennarTheme?.lennarDesignSystem?.lennarBlues?.lennarBlue,
                marginRight: '36px',
                ...(isDrawerOpen && {
                  display: 'none',
                }),
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex' }}>
              <LennarLogoComponent />
              <LocalBranchIndicator />
            </Box>
            <ReleaseNoteBanner />
            <UserAvatarAndSettingsMenu />
          </Box>
        </Toolbar>
      </AppBar>
      {!isOnDetailedPlan && (
        <Drawer
          variant="permanent"
          open={isDrawerOpen}
          sx={{ height: '100vh' }}
        >
          <Toolbar
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
              px: [1],
            }}
          >
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component="nav">
            <ListItemButton
              selected={selectedItem === DASHBOARD}
              onClick={() => {
                setSelectedItem(DASHBOARD)
                if (hasUnsavedPlanChanges()) {
                  setOpenDialog(true)
                  return
                }
                dispatch(setDrawerOpen(false))
                navigate('/')
              }}
            >
              <ListItemIcon>
                <Tooltip title="Dashboard">
                  <DashboardOutlinedIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
            <ListItemButton
              selected={selectedItem === SINGLE_FAMILY}
              onClick={() => {
                setSelectedItem(SINGLE_FAMILY)
                if (hasUnsavedPlanChanges()) {
                  setOpenDialog(true)
                  return
                }

                const tab =
                  checkSearchAndCompareMode() || isViewOnlyUser
                    ? PLANS_TABS.ALL_PLANS
                    : singleTabValue

                dispatch(setSinglePlansPageTab(tab))
                navigateToRoute(tab, familyTypes.SINGLE)
                dispatch(resetPlanListing())
                dispatch(setDrawerOpen(false))
                dispatch(clearAllPlansActiveElevations())
              }}
            >
              <ListItemIcon>
                <Tooltip title={SINGLE_FAMILY_LABEL}>
                  <HouseIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary={SINGLE_FAMILY_LABEL} />
            </ListItemButton>
            <ListItemButton
              selected={selectedItem === MULTI_FAMILY}
              onClick={() => {
                setSelectedItem(MULTI_FAMILY)
                if (hasUnsavedPlanChanges()) {
                  setOpenDialog(true)
                  return
                }

                const tab = getMultiFamilyTab()
                const shellsTab = VIEW_ONLY_MULTI_FAMILY_TABS.SHELL_PLANS
                const unitsTab = VIEW_ONLY_MULTI_FAMILY_TABS.UNIT_PLANS
                const allPlansTab = PLANS_TABS.ALL_PLANS

                if (checkSearchAndCompareMode() || isViewOnlyUser) {
                  if (tab === allPlansTab) {
                    dispatch(setSelectedTab(shellsTab))
                    navigateToRoute(shellsTab, familyTypes.MULTI)
                  } else {
                    dispatch(setSelectedTab(tab))
                    navigateToRoute(tab, familyTypes.MULTI)
                  }
                } else {
                  if (tab === shellsTab || tab === unitsTab) {
                    dispatch(setSelectedTab(allPlansTab))
                    navigateToRoute(allPlansTab, familyTypes.MULTI)
                  } else {
                    dispatch(setSelectedTab(tab))
                    navigateToRoute(tab, familyTypes.MULTI)
                  }
                }
                dispatch(resetPlanListing())
                dispatch(setDrawerOpen(false))
                dispatch(clearAllPlansActiveElevations())
              }}
            >
              <ListItemIcon>
                <Tooltip title={MULTI_FAMILY_LABEL}>
                  <ApartmentIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary={MULTI_FAMILY_LABEL} />
            </ListItemButton>
            {shouldShowViewOnlyPage && (
              <ListItemButton
                selected={selectedItem === FAVORITES}
                onClick={() => {
                  setSelectedItem(FAVORITES)
                  navigateToFavoritesRoute()
                  dispatch(setDrawerOpen(false))
                  dispatch(clearAllPlansActiveElevations())
                }}
              >
                <ListItemIcon>
                  <Tooltip title={FAVORITES_LABEL}>
                    <FavoriteIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText primary={FAVORITES_LABEL} />
              </ListItemButton>
            )}
            {isCorporateCreateUser && (
              <ListItemButton
                selected={selectedItem === PENDING_APPROVALS}
                onClick={() => {
                  setSelectedItem(PENDING_APPROVALS)
                  if (hasUnsavedPlanChanges()) {
                    setOpenDialog(true)
                    return
                  }
                  dispatch(resetPlanListing())
                  dispatch(setDrawerOpen(false))
                  navigate(ROUTES.APPROVALS)
                  dispatch(clearAllPlansActiveElevations())
                }}
              >
                <ListItemIcon>
                  <Tooltip title={PENDING_APPROVALS_LABEL}>
                    <ApprovalIcon />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText primary={PENDING_APPROVALS_LABEL} />
              </ListItemButton>
            )}
            {/* LTGC-5711: regional plan approvers are a distinct set from the
                core approvers, and may hold no Plan Library role beyond their
                approver record. */}
            {isCurrentUserARegionalApprover && (
              <ListItemButton
                selected={selectedItem === REGIONAL_APPROVALS}
                onClick={() => {
                  setSelectedItem(REGIONAL_APPROVALS)
                  if (hasUnsavedPlanChanges()) {
                    setOpenDialog(true)
                    return
                  }
                  dispatch(resetPlanListing())
                  dispatch(setDrawerOpen(false))
                  navigate(ROUTES.REGIONAL_APPROVALS)
                  dispatch(clearAllPlansActiveElevations())
                }}
              >
                <ListItemIcon>
                  <Tooltip title={REGIONAL_APPROVALS_LABEL}>
                    <Badge
                      // undefined rather than 0 keeps the digit out of the DOM
                      // entirely, so nothing is announced to a screen reader
                      // when there is nothing pending.
                      badgeContent={pendingRegionalApprovalCount || undefined}
                      color="error"
                      overlap="circular"
                    >
                      <PendingActionsIcon />
                    </Badge>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText primary={REGIONAL_APPROVALS_LABEL} />
              </ListItemButton>
            )}
            {(isCurrentUserAnApprover || isDivisionCreateUser) &&
              getEnvVar('REACT_APP_PENDING_PLANS_ENABLED') === 'true' && (
                <ListItemButton
                  selected={selectedItem === PENDING_PLANS}
                  onClick={() => {
                    setSelectedItem(PENDING_PLANS)
                    if (hasUnsavedPlanChanges()) {
                      setOpenDialog(true)
                      return
                    }
                    dispatch(resetPlanListing())
                    dispatch(setDrawerOpen(false))
                    navigate(ROUTES.PENDING_PLANS)
                    dispatch(clearAllPlansActiveElevations())
                  }}
                >
                  <ListItemIcon>
                    <Tooltip title={PENDING_PLANS_LABEL}>
                      <ChecklistIcon />
                    </Tooltip>
                  </ListItemIcon>
                  <ListItemText primary={PENDING_PLANS_LABEL} />
                </ListItemButton>
              )}
            <ListItemButton
              selected={selectedItem === EXPORTS}
              onClick={() => {
                setSelectedItem(EXPORTS)
                if (hasUnsavedPlanChanges()) {
                  setOpenDialog(true)
                  return
                }
                dispatch(resetPlanListing())
                dispatch(setDrawerOpen(false))
                navigate(ROUTES.EXPORTS)
                dispatch(clearAllPlansActiveElevations())
              }}
            >
              <ListItemIcon>
                <Tooltip title={EXPORTS_LABEL}>
                  <FileDownloadIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary={EXPORTS_LABEL} />
            </ListItemButton>
          </List>
          <SupportActions variant="nav" />
        </Drawer>
      )}

      {isOnDetailedPlan && (
        <Drawer
          variant="permanent"
          open={true}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              height: 'calc(100vh - 64px)',
              left: 0,
              overflowY: 'auto',
              position: 'fixed',
              top: 64,
              width: drawerWidth,
              zIndex: 1200,
            },
          }}
        >
          <List>
            <ListItemButton onClick={() => navigate(-1)}>
              <ListItemIcon>
                <ArrowBackIcon />
              </ListItemIcon>
              <ListItemText primary="Back" />
            </ListItemButton>
            <Divider />
            <ListItemButton
              selected
              sx={{
                alignItems: 'center',
                backgroundColor: '#eaf5fe',
                borderLeft: '4px solid #3489EB',
                display: 'flex',
                gap: '10px',
                justifyContent: 'center',
                padding: '16px 30px',
              }}
            >
              <ListItemText primary="Plan Overview" />
            </ListItemButton>
            <Divider />
          </List>
        </Drawer>
      )}
      <DialogBox
        open={openDialog}
        onIconClose={closeDialog}
        onClose={handleCloseDialog}
        onActionClick={handleSaveAsDraft}
        modalType={
          pathname.includes('submit')
            ? 'Back Button Submit Dialog'
            : 'Back Button Dialog'
        }
      />
    </>
  )
}
